import { useState, useEffect, useRef } from 'react';
import ApiHandler from "./../../../../../services/api/ApiHandler";
import apiPayload from './../../../../../services/api/ApiPayload';
import localStorageService from './../../../../../helpers/store/StorageService';
import CloudLoader from './../../../loader/Loader_02';
import VirtualKeyboard from './../../../virtual/Keyboard';

const ReceiveBTC = ({ initError, initLoader }) => {
  const hasRun = useRef(false);  // Ref to track if effect has run
  let { data, loading, error, handlePostApi } = ApiHandler();
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 1023px)').matches);
  const [activeInput, setActiveInput] = useState(null);
  const inputRefs = useRef({});  // Refs for position
  const [step, setStep] = useState(1);  // Current step (1 to 8)
  const [formData, setFormData] = useState({btcAmount: ''});
  const [errors, setErrors] = useState({});  // Field-specific errors
  const [isLoading, setIsLoading] = useState(false);  // For API loading
  const [serverError, setServerError] = useState(null);  // For API uniqueness errors

  const [channelName, setChannelName] = useState(null);
  const [btcAddress, setBTCAddress] = useState(null);
  const [submitSucess, setSubmitSucess] = useState('');
  const [submitSucessAlert, setSubmitSucessAlert] = useState('');

  // Validation functions (adapted from sign up, with min 2 symbols check)
  const validateBTCAmount = (btcAmount) => parseFloat(btcAmount.trim()) > 0.000001;

  // Validate current step's fields (min 2 symbols for enable)
  const validateCurrentStep = () => {
    let newErrors = {};
    switch (step) {
      case 1:
        break;
      case 2:
        break;
      case 3:
        if (!validateBTCAmount(formData.btcAmount)) newErrors.btcAmount = 'Enter valid BTC amount (> 0.000001)';
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate all fields before API submit
  const validateAll = () => {
    let newErrors = {};
    if (!validateBTCAmount(formData.btcAmount)) newErrors.btcAmount = 'Enter valid BTC amount (> 0.000001)';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Next button handler
  const handleNext = () => {
    if (validateCurrentStep()) {
      setStep((prev) => prev + 1);
      setErrors({});
      setServerError(null);
    }
  };

  // Previous button handler
  const handlePrevious = () => {
    setStep((prev) => prev - 1);
    setErrors({});
    setServerError(null);
  };

  useEffect(() => {
    if (hasRun.current) return;  // Skip if already run
    let isMounted = true;
    if (isMounted) {
      setIsLoading(true);
      setServerError(null);
      try {
        apiPayload.set('load.pageId', 'Page30009');
        apiPayload.set('load.redirectRoute', '/auth/user/receive');
        apiPayload.set('load.data.userId', localStorageService.get('active', -1, 'int'));
        handlePostApi("/v1/receive", apiPayload.fetchPayload());
      } catch (err) {
        setServerError({ general: 'API error', error: error });
        console.warn(err);
      } finally {
        setIsLoading(false);
      }
    }

    hasRun.current = true;  // Flag as run

    // Cleanup to prevent updates after unmount
    return () => {
      isMounted = false;
    };
    // TODO : Clear the warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Submit handler
  const handleSubmit = async () => {
    if (validateAll()) {
      setIsLoading(true);
      setServerError(null);
      try {
        apiPayload.set('load.pageId', 'Page30009');
        apiPayload.set('load.redirectRoute', '/auth/user/receive');
        apiPayload.set('load.data.userId', localStorageService.get('active', -1, 'int'));
        apiPayload.set('load.data.channelName', channelName);
        apiPayload.set('load.data.receiveAddress', btcAddress);
        apiPayload.set('load.data.btcTransaction', formData.btcAmount);
        handlePostApi("/v1/receive/submit", apiPayload.fetchPayload());
      } catch (err) {
        setServerError({ general: 'API error', error: error });
        console.warn(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(btcAddress).then(() => {
      console.info('BTC Address copied!');
    }).catch(() => {
      console.warn('Failed to copy!');
    });
  };

  // Render step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <label><b>Transaction Channel</b></label>
            <br/>
            <p className='account-data-value'>{channelName}</p>
          </div>
        );
      case 2:
        return (
          <div>
            <label><b>Receive BTC Address </b></label>
            <label>(BTC Network)</label>
            <br/>
            <p className='account-data-value'>1EyK*****j7sg</p>
            <button className="btc-copy-btn" onClick={handleCopy}>Copy</button>
          </div>
        );
      case 3:
        return (
          <div>
            <label><b>BTC Amount</b></label>
            <br/>
            <input ref={(el) => (inputRefs.current['btcAmount'] = el)} name="btcAmount" value={formData.btcAmount} onChange={handleChange} onBlur={(e) => { if (isMobile && !e.relatedTarget?.closest('.virtual-keyboard')) { handleCloseKeyboard(); } }} onClick={() => isMobile && setActiveInput('btcAmount')} onFocus={() => isMobile && setActiveInput('btcAmount')} readOnly={isMobile}/>
            {errors.btcAmount && <span className="error">{errors.btcAmount}</span>}
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = () => setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Handle virtual key press
  const handleKeyPress = (key) => {
    if (activeInput) {
      let value = formData[activeInput];
      if (key === 'backspace') value = value.slice(0, -1);
      else value += key;
      setFormData((prev) => ({ ...prev, [activeInput]: value }));
    }
  };

  // Handle keyboard close
  const handleCloseKeyboard = () => setActiveInput(null);

  useEffect(() => {
    if (data && data.code === "200") {
      if (data.result.api_request_data.channel_name) {
        setChannelName(data.result.api_request_data.channel_name);
      }

      if (data.result.api_request_data.btc_receive_address) {
        setBTCAddress(data.result.api_request_data.btc_receive_address);
      }

      if (data.result.api_request_data.submit_success_header) {
        setSubmitSucess(data.result.api_request_data.submit_success_header);
      }

      if (data.result.api_request_data.submit_success_alert) {
        setSubmitSucessAlert(data.result.api_request_data.submit_success_alert);
      }
    } else if (data && data.code !== "200") {
      setServerError({ general: data.result.api_request_data.error });
    }
  }, [data]);

  return (
    <div className="join-page" style={{ position: 'relative' }}>
      {initLoader && <CloudLoader />}
      {isLoading && <CloudLoader />}
      {loading && <CloudLoader />}
      {(submitSucess || submitSucessAlert) && (<div>
        <p className='submit-sucess'><strong><b>{submitSucess}</b></strong></p>
        <br/>
        <p className='submit-sucess-alert'><strong><b>{submitSucessAlert}</b></strong></p>
        <br/>
        <a href="/getting-started">Go Home</a>
      </div>)}
      <br/>
      {!submitSucess && !submitSucessAlert && (<div>
        <p style={{ color: "rgb(208, 255, 0)" }}><b>Request</b></p>
        <br/>
        <h2>Receive BTC</h2>
        <hr style={{border:'none', height:'2px', background:'linear-gradient(to right, #e600c7 0%, #940180 25%, rgb(0, 0, 0) 100%)', boxShadow: 'rgba(0, 0, 0, 0.5) 0px 1px 3px inset', margin:'20px 0px', width:'100%'}} />
        <p style={{ color: "rgba(72, 255, 0, 1)" }}><b>Secured 3-layer transaction</b></p>
        <hr style={{border:'none', height:'2px', background:'linear-gradient(to right, #e600c7 0%, #940180 25%, rgb(0, 0, 0) 100%)', boxShadow: 'rgba(0, 0, 0, 0.5) 0px 1px 3px inset', margin:'20px 0px', width:'100%'}} />
        <p><b>{step}</b> / 3</p>
        <br/>
        {renderStep()}
        {activeInput && isMobile && (
          <VirtualKeyboard
            onKeyPress={handleKeyPress}
            onClose={handleCloseKeyboard}
            isVisible={isMobile && !!activeInput}
          />
        )}
        {serverError?.general && <span className="error">{serverError.general}</span>}
        <div className="buttons">
          {step > 1 && <button className="btn" onClick={handlePrevious}>Previous</button>}
          {step < 3 && <button className="btn" onClick={handleNext} disabled={isLoading}>Next</button>}
          {step === 3 && <button className="btn" onClick={handleSubmit} disabled={isLoading}>Submit Request</button>}
        </div>
      </div>)}
    </div>
  );
};

export default ReceiveBTC;
